import React, { useState } from 'react';
import { GripVertical } from 'lucide-react';
import { BLOCK_REGISTRY } from './registry';
import HeroRenderer from './hero/HeroRenderer';
import TextRenderer from './text/TextRenderer';
import ImageRenderer from './image/ImageRenderer';
import FeaturesRenderer from './features/FeaturesRenderer';
import CTARenderer from './cta/CTARenderer';
import FooterRenderer from './footer/FooterRenderer';
import FormRenderer from './form/FormRenderer';
import TestimonialsRenderer from './testimonials/TestimonialsRenderer';
import PricingRenderer from './pricing/PricingRenderer';
import BookingRenderer from './booking/BookingRenderer';
import FAQRenderer from './faq/FAQRenderer';
import StatsRenderer from './stats/StatsRenderer';
import LogoCloudRenderer from './logo_cloud/LogoCloudRenderer';
import VideoRenderer from './video/VideoRenderer';
import DividerRenderer from './divider/DividerRenderer';
import GalleryRenderer from './gallery/GalleryRenderer';
import HtmlRenderer from './html/HtmlRenderer';

const RENDERERS = {
  hero: HeroRenderer,
  text: TextRenderer,
  image: ImageRenderer,
  features: FeaturesRenderer,
  cta: CTARenderer,
  footer: FooterRenderer,
  form: FormRenderer,
  testimonials: TestimonialsRenderer,
  pricing: PricingRenderer,
  booking: BookingRenderer,
  faq: FAQRenderer,
  stats: StatsRenderer,
  logo_cloud: LogoCloudRenderer,
  video: VideoRenderer,
  divider: DividerRenderer,
  gallery: GalleryRenderer,
  html: HtmlRenderer,
};

/**
 * Universal block renderer. Dispatches to the correct type-specific renderer
 * and wraps with editing chrome (selection ring, hover overlay, drag handle).
 *
 * Props:
 *   block      - A PageBlock object { block_id, block_type, variant, props }
 *   isEditing  - Whether the page is in edit mode
 *   isSelected - Whether this block is currently selected
 *   onSelect   - Callback when this block is clicked in edit mode
 */
export default function BlockRenderer({ block, isEditing = false, isSelected = false, onSelect }) {
  const [isHovered, setIsHovered] = useState(false);

  if (!block || !block.block_type) {
    return null;
  }

  const Renderer = RENDERERS[block.block_type];
  const definition = BLOCK_REGISTRY[block.block_type];

  if (!Renderer) {
    return (
      <div
        data-block-id={block.block_id}
        className="py-8 px-4 text-center text-stone-400 bg-stone-50 border border-dashed border-stone-300 rounded-lg"
      >
        Unknown block type: <code className="text-stone-600">{block.block_type}</code>
      </div>
    );
  }

  const content = (
    <Renderer
      props={block.props || {}}
      variant={block.variant || definition?.variants?.[0] || 'default'}
      isEditing={isEditing}
    />
  );

  // In view mode, render the block without any editing chrome
  if (!isEditing) {
    return (
      <div data-block-id={block.block_id}>
        {content}
      </div>
    );
  }

  // In edit mode, wrap with selection ring, hover overlay, and drag handle
  return (
    <div
      data-block-id={block.block_id}
      className={`relative group cursor-pointer transition-all ${
        isSelected
          ? 'ring-2 ring-blue-500 ring-offset-2 rounded-lg'
          : isHovered
          ? 'ring-1 ring-blue-300 ring-offset-1 rounded-lg'
          : ''
      }`}
      onClick={(e) => {
        e.stopPropagation();
        onSelect?.(block.block_id);
      }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {content}

      {/* Hover overlay with block type label and drag handle */}
      {(isHovered || isSelected) && (
        <div className="absolute top-0 left-0 right-0 flex items-center justify-between pointer-events-none z-10">
          <div className="flex items-center gap-1 bg-blue-500 text-white text-[10px] font-medium px-2 py-1 rounded-br-lg rounded-tl-lg pointer-events-auto">
            <GripVertical className="w-3 h-3 cursor-grab" />
            <span>{definition?.label || block.block_type}</span>
          </div>
        </div>
      )}
    </div>
  );
}
